/**
 * Generic asset extraction helpers for property listing pages.
 *
 * Pulls photo, floorplan, PDF, and document URLs out of raw HTML using
 * source-agnostic signals (JSON-LD image[], OpenGraph og:image, link
 * rel="image_src", <picture><img>). Adapters can layer site-specific
 * extractors on top via `AdapterDefinition.extractAssets`.
 *
 * Caps: max 10 photos per listing. Floorplans, PDFs, and documents are
 * not capped at the generic layer — adapter-specific extractors apply
 * their own caps (REA: 1 floorplan).
 */

export type AssetType = "photo" | "floorplan" | "pdf" | "document";

export interface ExtractedAsset {
  type: AssetType;
  sourceUrl: string;
  /** 0-based position on the listing page. Only set for photos. */
  indexInListing?: number;
}

const PHOTO_EXTENSION_RE = /\.(png|jpg|jpeg|webp|avif|gif)(?:\?|$|#)/i;
const PDF_EXTENSION_RE = /\.pdf(?:\?|$|#)/i;
const MAX_PHOTOS = 10;

/**
 * Safely resolve a relative URL against the listing page's base URL.
 * Returns null when either input is unparseable (data: URIs, javascript:,
 * malformed strings) so callers can skip silently.
 */
function resolveUrl(rel: string, baseUrl: string): string | null {
  if (!rel) return null;
  const trimmed = rel.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("data:") || trimmed.startsWith("javascript:")) {
    return null;
  }
  try {
    return new URL(trimmed, baseUrl).toString();
  } catch {
    return null;
  }
}

/**
 * Pull JSON-LD blocks out of HTML and return the parsed image[] values
 * from any Product / RealEstateListing / Place schema. Tolerant of malformed
 * JSON — bad blocks are skipped silently.
 */
function extractJsonLdImages(html: string, baseUrl: string): string[] {
  const urls: string[] = [];
  const re = /<script[^>]+type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
  let match: RegExpExecArray | null;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex-loop idiom
  while ((match = re.exec(html)) !== null) {
    const raw = match[1].trim();
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      const nodes = Array.isArray(parsed) ? parsed : [parsed];
      for (const node of nodes) {
        const image = (node as Record<string, unknown>)?.image;
        if (!image) continue;
        if (typeof image === "string") {
          const resolved = resolveUrl(image, baseUrl);
          if (resolved) urls.push(resolved);
        } else if (Array.isArray(image)) {
          for (const entry of image) {
            if (typeof entry === "string") {
              const resolved = resolveUrl(entry, baseUrl);
              if (resolved) urls.push(resolved);
            } else if (entry && typeof entry === "object") {
              const url = (entry as Record<string, unknown>).url;
              if (typeof url === "string") {
                const resolved = resolveUrl(url, baseUrl);
                if (resolved) urls.push(resolved);
              }
            }
          }
        } else if (typeof image === "object") {
          const url = (image as Record<string, unknown>).url;
          if (typeof url === "string") {
            const resolved = resolveUrl(url, baseUrl);
            if (resolved) urls.push(resolved);
          }
        }
      }
    } catch {
      // skip malformed JSON-LD block
    }
  }
  return urls;
}

function extractMetaImages(html: string, baseUrl: string): string[] {
  const urls: string[] = [];
  // og:image / og:image:secure_url / twitter:image
  const metaRe =
    /<meta[^>]+(?:property|name)=["'](?:og:image(?::secure_url)?|twitter:image)["'][^>]*content=["']([^"']+)["'][^>]*>/gi;
  let m: RegExpExecArray | null;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex-loop idiom
  while ((m = metaRe.exec(html)) !== null) {
    const resolved = resolveUrl(m[1], baseUrl);
    if (resolved) urls.push(resolved);
  }
  // <meta content="..." property="og:image"> (reversed attribute order)
  const reverseRe =
    /<meta[^>]+content=["']([^"']+)["'][^>]*(?:property|name)=["'](?:og:image(?::secure_url)?|twitter:image)["'][^>]*>/gi;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex-loop idiom
  while ((m = reverseRe.exec(html)) !== null) {
    const resolved = resolveUrl(m[1], baseUrl);
    if (resolved) urls.push(resolved);
  }
  return urls;
}

function extractLinkImageSrc(html: string, baseUrl: string): string[] {
  const urls: string[] = [];
  const re = /<link[^>]+rel=["']image_src["'][^>]*href=["']([^"']+)["'][^>]*>/gi;
  let m: RegExpExecArray | null;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex-loop idiom
  while ((m = re.exec(html)) !== null) {
    const resolved = resolveUrl(m[1], baseUrl);
    if (resolved) urls.push(resolved);
  }
  return urls;
}

function extractPictureImages(html: string, baseUrl: string): string[] {
  const urls: string[] = [];
  const re = /<picture[^>]*>([\s\S]*?)<\/picture>/gi;
  let block: RegExpExecArray | null;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex-loop idiom
  while ((block = re.exec(html)) !== null) {
    const imgRe = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
    let img: RegExpExecArray | null;
    // biome-ignore lint/suspicious/noAssignInExpressions: standard regex-loop idiom
    while ((img = imgRe.exec(block[1])) !== null) {
      const src = img[1];
      if (PHOTO_EXTENSION_RE.test(src)) {
        const resolved = resolveUrl(src, baseUrl);
        if (resolved) urls.push(resolved);
      }
    }
  }
  return urls;
}

function extractPdfLinks(html: string, baseUrl: string): string[] {
  const urls: string[] = [];
  const re = /<a[^>]+href=["']([^"']+\.pdf(?:\?[^"']*)?)["'][^>]*>/gi;
  let m: RegExpExecArray | null;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex-loop idiom
  while ((m = re.exec(html)) !== null) {
    if (PDF_EXTENSION_RE.test(m[1])) {
      const resolved = resolveUrl(m[1], baseUrl);
      if (resolved) urls.push(resolved);
    }
  }
  return urls;
}

/**
 * Generic extraction: pulls photos + PDFs out of an arbitrary property
 * listing page. Returns at most MAX_PHOTOS photos and all PDFs found,
 * deduped by source URL. Floorplans are not detected at this generic
 * layer — they require site-specific signals (testid, alt text).
 */
export function extractGenericAssets(html: string, baseUrl: string): ExtractedAsset[] {
  const assets: ExtractedAsset[] = [];
  const seen = new Set<string>();

  const photoUrls: string[] = [];
  for (const url of extractJsonLdImages(html, baseUrl)) photoUrls.push(url);
  for (const url of extractMetaImages(html, baseUrl)) photoUrls.push(url);
  for (const url of extractLinkImageSrc(html, baseUrl)) photoUrls.push(url);
  for (const url of extractPictureImages(html, baseUrl)) photoUrls.push(url);

  let photoIndex = 0;
  for (const url of photoUrls) {
    if (seen.has(url)) continue;
    if (!PHOTO_EXTENSION_RE.test(url)) continue;
    seen.add(url);
    assets.push({ type: "photo", sourceUrl: url, indexInListing: photoIndex });
    photoIndex += 1;
    if (photoIndex >= MAX_PHOTOS) break;
  }

  for (const url of extractPdfLinks(html, baseUrl)) {
    if (seen.has(url)) continue;
    seen.add(url);
    assets.push({ type: "pdf", sourceUrl: url });
  }

  return assets;
}

/** Re-export the photo cap so adapters can mirror generic capping rules. */
export const GENERIC_PHOTO_CAP = MAX_PHOTOS;
